import ActionDialog from "@dashboard/components/ActionDialog/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { ListPageLayout } from "@dashboard/components/Layouts/List/Root";
import {
  type AtticAssetFragment,
  useAtticAssetDeleteMutation,
  useAtticAssetDirectoryDetailsQuery,
  useAtticAssetListQuery,
} from "@dashboard/graphql";
import useLocalPaginator, { useLocalPaginationState } from "@dashboard/hooks/useLocalPaginator";
import { useNotifier } from "@dashboard/hooks/useNotifier/useNotifier";
import { sectionNames } from "@dashboard/intl";
import { getMutationState } from "@dashboard/misc";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { Skeleton, Text } from "@saleor/macaw-ui-next";
import { useCallback, useRef, useState } from "react";
import { useIntl } from "react-intl";

import AtticAssetDirectoryPage, {
  AtticAssetDirectoryMissing,
} from "../components/AtticAssetDirectoryPage/AtticAssetDirectoryPage";
import { AtticAssetUploader } from "../components/AtticAssetUploadCard/AtticAssetUploader";
import { getAtticAssetDeleteRefusalMessage, isAtticAssetGoneRefusal } from "../errors";
import { fileListMessages, sectionMessages } from "../messages";
import { AtticAssetsDestinationIcon } from "../topNav";
import { atticAssetDirectoryListPath } from "../urls";

const PAGINATE_BY = 20;

export const AtticAssetDirectoryDetailsView = ({ id }: { id: string }) => {
  const intl = useIntl();
  const notify = useNotifier();
  const [query, setQuery] = useState("");
  const [rowNumber, setRowNumber] = useState(PAGINATE_BY);
  const [paginationState, setPaginationState] = useLocalPaginationState(rowNumber);
  const paginate = useLocalPaginator(setPaginationState);
  const [deleting, setDeleting] = useState<AtticAssetFragment | null>(null);
  const directoryQuery = useAtticAssetDirectoryDetailsQuery({
    variables: { id },
    fetchPolicy: "cache-and-network",
  });
  const assetsQuery = useAtticAssetListQuery({
    variables: {
      directory: id,
      ...paginationState,
      ...(query ? { search: query } : {}),
    },
    fetchPolicy: "cache-and-network",
  });
  const { refetch: refetchDirectory } = directoryQuery;
  const { refetch: refetchAssets } = assetsQuery;
  // Counts, sizes and the store's usage all move with a file: refresh both.
  const refresh = useCallback(() => {
    refetchDirectory();
    refetchAssets();
  }, [refetchDirectory, refetchAssets]);
  // The house query reads with errorPolicy "all", so a refetch that fails
  // (the throttled one the uploader asks for, say) can answer null for both
  // fields alongside its errors. Once the page has shown, that keeps the last
  // good answer on screen, and with it the uploader and its queue: only a
  // clean answer that the directory is gone (null, no errors) unmounts them.
  const lastLoaded = useRef<{
    id: string;
    directory: NonNullable<NonNullable<typeof directoryQuery.data>["atticAssetDirectory"]>;
    store: NonNullable<NonNullable<typeof directoryQuery.data>["atticAssetStore"]>;
  } | null>(null);
  const answered = directoryQuery.data;
  const failed = !!directoryQuery.error;

  if (answered?.atticAssetDirectory && answered.atticAssetStore) {
    lastLoaded.current = {
      id,
      directory: answered.atticAssetDirectory,
      store: answered.atticAssetStore,
    };
  } else if (answered && answered.atticAssetDirectory === null && !failed) {
    lastLoaded.current = null;
  }

  // Another directory's page (the route's id changed) never shows this one's.
  const shown = lastLoaded.current?.id === id ? lastLoaded.current : null;
  // A refusal is one toast in merchant words; the wrapper still handles a
  // lost session and read-only mode. A file already gone closes the dialog
  // and refreshes, so its stale row disappears.
  const [deleteAsset, deleteOpts] = useAtticAssetDeleteMutation({
    disableNestedErrorHandling: true,
    onCompleted: result => {
      const [refusal] = result.atticAssetDelete?.errors ?? [];

      if (refusal) {
        notify({
          status: isAtticAssetGoneRefusal(refusal) ? "info" : "error",
          text: getAtticAssetDeleteRefusalMessage(refusal, intl, "file"),
        });

        if (isAtticAssetGoneRefusal(refusal)) {
          setDeleting(null);
          refresh();
        }

        return;
      }

      notify({ status: "success", text: intl.formatMessage(sectionMessages.fileDeleted) });
      setDeleting(null);
      refresh();
    },
  });
  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    assetsQuery.data?.atticAssets?.pageInfo,
    paginationState,
  );

  if (!shown) {
    // Gone only on a clean answer; an error, or a store the API could not
    // read, is a load failure.
    if (answered && answered.atticAssetDirectory === null && !failed) {
      return <AtticAssetDirectoryMissing />;
    }

    if (!directoryQuery.loading && (answered || failed)) {
      return <AtticAssetDirectoryMissing loadFailed />;
    }

    return (
      <ListPageLayout>
        <TopNav
          href={atticAssetDirectoryListPath}
          hrefIcon={<AtticAssetsDestinationIcon />}
          hrefTitle={intl.formatMessage(sectionMessages.allDirectories)}
          title={intl.formatMessage(sectionNames.atticDigitalAssets)}
        />
        <Skeleton />
      </ListPageLayout>
    );
  }

  const { directory, store } = shown;

  return (
    <>
      <AtticAssetDirectoryPage
        directory={directory}
        assets={
          assetsQuery.loading && !assetsQuery.data
            ? undefined
            : (mapEdgesToItems(assetsQuery.data?.atticAssets) ?? [])
        }
        assetsLoadFailed={!assetsQuery.data && !assetsQuery.loading && !!assetsQuery.error}
        query={query}
        rowNumber={rowNumber}
        hasNextPage={pageInfo?.hasNextPage ?? false}
        hasPreviousPage={pageInfo?.hasPreviousPage ?? false}
        uploader={
          // Its own component: the queue's state and progress stay out of
          // this view, so the file table never re-renders per progress tick.
          // Mounted while the directory is shown, writable or not, so a
          // refresh that finds it claimed mid-batch never drops the queue;
          // leaving it asks first (useAtticAssetUploads), then aborts what is in
          // flight. Keyed by the directory: the route keeps this view mounted
          // across directories, and one directory's queue never shows on, or
          // claims names in, another's.
          <AtticAssetUploader
            key={id}
            directoryId={id}
            maxUploadBytes={store.maxUploadBytes}
            writable={directory.writable}
            onUploaded={refresh}
          />
        }
        onQueryChange={value => {
          setQuery(value);
          setPaginationState({});
        }}
        onRowNumberChange={value => {
          setRowNumber(value);
          setPaginationState({});
        }}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onDelete={setDeleting}
      />
      <ActionDialog
        open={!!deleting}
        variant="delete"
        title={intl.formatMessage(fileListMessages.deleteTitle)}
        confirmButtonState={getMutationState(
          deleteOpts.called,
          deleteOpts.loading,
          deleteOpts.data?.atticAssetDelete?.errors ?? [],
        )}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && deleteAsset({ variables: { id: deleting.id } })}
      >
        {deleting && (
          <Text size={3}>
            {intl.formatMessage(fileListMessages.deleteContent, { filename: deleting.path })}
          </Text>
        )}
      </ActionDialog>
    </>
  );
};

export default AtticAssetDirectoryDetailsView;
